import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useLead } from '../contexts/LeadContext';
import { Reveal } from './Reveal';
import { trackVehicleInterest } from '../tracking';
import { scrollToLeadForm } from '../utils/scroll';

/**
 * Second entry point into the SAME lead flow, for a different intent.
 * It never displays, estimates, or implies a dollar value for the trade — no numbers.
 */
export function TradeIn() {
  const {
    t
  } = useLanguage();
  const {
    setPresetIntent,
    setTradeIn
  } = useLead();
  const [values, setValues] = useState({
    year: '',
    make: '',
    model: ''
  });
  const [error, setError] = useState('');
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!values.year.trim() || !values.make.trim() || !values.model.trim()) {
      setError(t('tradein.err'));
      return;
    }
    setError('');
    setTradeIn({
      year: values.year.trim(),
      make: values.make.trim(),
      model: values.model.trim()
    });
    setPresetIntent('trade-in-buy');
    trackVehicleInterest('trade-in');
    scrollToLeadForm();
  };
  const fields = [{
    id: 'tradeYear',
    key: 'year' as const,
    label: t('tradein.year'),
    mode: 'numeric' as const
  }, {
    id: 'tradeMake',
    key: 'make' as const,
    label: t('tradein.make'),
    mode: 'text' as const
  }, {
    id: 'tradeModel',
    key: 'model' as const,
    label: t('tradein.model'),
    mode: 'text' as const
  }];
  return <section className="w-full bg-offwhite" data-id="element-250" data-mp="2y4brwony0m0b">
      <div className="mx-auto w-full max-w-4xl px-4 py-16 sm:px-6 md:py-20" data-id="element-251" data-mp="2y1z3ptrldfet">
        <Reveal data-id="element-252" data-mp="2t3e1xpwoyno9">
          <h2 className="font-display text-3xl font-extrabold uppercase leading-[1.04] tracking-[-0.02em] text-ink sm:text-4xl" data-id="element-253" data-mp="2t71v2ny8582g">
            {t('tradein.title')}
          </h2>
          <div aria-hidden="true" className="mt-3 h-[3px] w-12 bg-kred" data-id="element-254" data-mp="2k5vbu746xj5i" />
          <p className="mt-4 max-w-xl text-base leading-relaxed text-body" data-id="element-255" data-mp="mesrq0uoc68d">{t('tradein.sub')}</p>
        </Reveal>

        <Reveal delay={0.1} data-id="element-256" data-mp="ie8tetyd6wkn">
          <form onSubmit={handleSubmit} noValidate className="mt-8" data-id="element-257" data-mp="iatcdll4rdgl">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-id="element-258" data-mp="2buuzoelip2a1">
              {fields.map(field => <div key={field.id} data-id="element-259" data-mp="2gnxvktdronte">
                  <label htmlFor={field.id} className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-muted" data-id="element-260" data-mp="22uyhmvtmv1ny">
                    {field.label}
                  </label>
                  <input id={field.id} name={field.id} type="text" inputMode={field.mode} value={values[field.key]} onChange={e => {
                setValues(prev => ({
                  ...prev,
                  [field.key]: e.target.value
                }));
                setError('');
              }} aria-invalid={Boolean(error)} aria-describedby={error ? 'tradein-error' : undefined} className="min-h-[52px] w-full border border-ink/20 bg-white px-4 text-base text-body focus:border-ink focus:outline-none" data-id="element-261" data-mp="2unfonky3in8k" />
                </div>)}
            </div>

            {error ? <p id="tradein-error" role="alert" className="mt-3 text-sm text-kred" data-id="element-262" data-mp="1gmb01xtaerl3">
                {error}
              </p> : null}

            <button type="submit" className="mt-6 min-h-[52px] w-full bg-gold px-6 text-sm font-bold uppercase tracking-[0.12em] text-golddeep transition-transform hover:-translate-y-[1px] sm:w-auto sm:px-10" data-id="element-263" data-mp="1bnzptlz2yf2c">
              {t('tradein.cta')}
            </button>
            <p className="mt-3 text-xs text-muted" data-id="element-264" data-mp="xozzm9vx5s42">{t('tradein.note')}</p>
          </form>
        </Reveal>
      </div>
    </section>;
}